import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Play, Webhook } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { erpApi, formatDate } from "../services/erpApi";

const providers = ["Pacific Labs", "Shipra Labs", "SRL Diagnostics", "Metropolis", "External Lab"];

const emptyForm = {
  provider: "Pacific Labs",
  sample_id: "",
  patient_mobile: "",
  patient_name: "",
  test_name: "",
  report_pdf_url: "",
  external_report_id: "",
  remarks: "",
};

export default function Integrations() {
  const [form, setForm] = useState(emptyForm);
  const [samples, setSamples] = useState([]);
  const [response, setResponse] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    erpApi.samples().then((res) => setSamples(res.data)).catch(() => toast.error("Failed to load samples"));
  }, []);

  const selectedSample = samples.find((sample) => sample.id === form.sample_id);

  const payload = useMemo(() => ({
    provider: form.provider,
    patient_mobile: form.patient_mobile,
    patient_name: form.patient_name,
    test_name: form.test_name,
    report_pdf_url: form.report_pdf_url,
    external_report_id: form.external_report_id,
    remarks: form.remarks || `Mock report received from ${form.provider}`,
  }), [form]);

  const selectSample = (sampleId) => {
    const sample = samples.find((item) => item.id === sampleId);
    setForm({
      ...form,
      sample_id: sampleId,
      patient_mobile: sample?.patient_mobile || "",
      patient_name: sample?.patient_name || "",
      test_name: sample?.test_name || "",
      provider: sample?.lab_name || form.provider,
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.patient_mobile || !form.report_pdf_url) {
      toast.error("Patient mobile and report PDF URL are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await erpApi.attachLabReport(payload);
      setResponse(res.data);
      toast.success("Mock webhook delivered");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Webhook mock failed");
      setResponse(error.response?.data || { error: "Webhook mock failed" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-manrope text-2xl font-bold">Integrations</h1>
        <p className="text-sm text-hospital-text-muted">Mock lab webhook testing for Pacific, Shipra and future WhatsApp Business automation.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Webhook className="h-4 w-4 text-hospital-primary" />
              Lab Report Webhook Simulator
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Lab Provider</Label>
                <Select value={form.provider} onValueChange={(value) => setForm({ ...form, provider: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{providers.map((provider) => <SelectItem key={provider} value={provider}>{provider}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Match Existing Sample</Label>
                <Select value={form.sample_id || "manual"} onValueChange={(value) => selectSample(value === "manual" ? "" : value)}>
                  <SelectTrigger><SelectValue placeholder="Select sample" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual patient details</SelectItem>
                    {samples.map((sample) => (
                      <SelectItem key={sample.id} value={sample.id}>
                        {sample.patient_name} - {sample.test_name} - {sample.status?.replaceAll("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Patient Mobile</Label>
                <Input value={form.patient_mobile} onChange={(e) => setForm({ ...form, patient_mobile: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Patient Name</Label>
                <Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Test Name</Label>
                <Input value={form.test_name} onChange={(e) => setForm({ ...form, test_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>External Report ID</Label>
                <Input placeholder="PAC-2026-001" value={form.external_report_id} onChange={(e) => setForm({ ...form, external_report_id: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Report PDF URL</Label>
                <Input placeholder="https://lab.example.com/reports/patient-report.pdf" value={form.report_pdf_url} onChange={(e) => setForm({ ...form, report_pdf_url: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Remarks</Label>
                <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </div>
              <div className="flex justify-end md:col-span-2">
                <Button className="btn-primary" disabled={submitting}>
                  <Play className="mr-2 h-4 w-4" />
                  {submitting ? "Sending..." : "Send Mock Webhook"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">Mock Payload</CardTitle></CardHeader>
            <CardContent>
              <Textarea readOnly className="min-h-72 font-mono text-xs" value={JSON.stringify(payload, null, 2)} />
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">Matched Sample</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm text-hospital-text-secondary">
              {selectedSample ? (
                <>
                  <p><span className="font-semibold text-hospital-text-main">Patient:</span> {selectedSample.patient_name}</p>
                  <p><span className="font-semibold text-hospital-text-main">Test:</span> {selectedSample.test_name}</p>
                  <p><span className="font-semibold text-hospital-text-main">Lab:</span> {selectedSample.lab_name}</p>
                  <p><span className="font-semibold text-hospital-text-main">Expected:</span> {selectedSample.expected_date || formatDate(selectedSample.collection_date)}</p>
                </>
              ) : (
                <p>Select a sample or enter patient details manually.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">Webhook Response</CardTitle></CardHeader>
            <CardContent>
              <Textarea readOnly className="min-h-56 font-mono text-xs" value={response ? JSON.stringify(response, null, 2) : "No mock response yet."} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
